import React from 'react';
import { BriefcaseIcon, CreditCardIcon, KeyRoundIcon, LayersIcon, RulerIcon, ShoppingBagIcon, WalletIcon } from 'lucide-react';
import { Seo } from '../components/Seo';
import { ButtonLink, Eyebrow, Heading, NextPage, Prose, Section, TextLink } from '../components/ui';
import { images, site } from '../data/site';
const productgroepen = [{
  icon: WalletIcon,
  title: 'Portemonnees',
  text: 'Van compacte modellen die nauwelijks opvallen in een broekzak tot ruimere overslagportemonnees met plek voor briefgeld, munten en een flink aantal pasjes. Een leren portemonnee gaat jarenlang mee en krijgt met de tijd een eigen kleurverloop.'
}, {
  icon: CreditCardIcon,
  title: 'Pasjeshouders',
  text: 'Wie vooral pint en zelden contant betaalt, heeft aan een pasjeshouder van leer genoeg. Slank in de zak, overzichtelijk in gebruik en beschikbaar in uitvoeringen met een los muntvak of een uitschuifsysteem.'
}, {
  icon: BriefcaseIcon,
  title: 'Horeca lederwaren',
  text: 'Kelnerportemonnees, holsters en bijbehorende etuis die tegen een lange dienst kunnen. De indeling is gemaakt voor het werkritme achter de bar en op het terras: bonnen, briefgeld, munten, pen en telefoon binnen handbereik.'
}, {
  icon: LayersIcon,
  title: 'Leren accessoires',
  text: 'Kleine artikelen die het dagelijks gebruik prettiger maken: paspoorthoesjes, pennenzakken, etuis en andere leren accessoires die in tas, koffer of bureaulade een vaste plek krijgen.'
}, {
  icon: ShoppingBagIcon,
  title: 'Heuptasjes',
  text: 'Een leren heuptasje houdt uw handen vrij en uw spullen dichtbij. Praktisch tijdens een markt, evenement of dagje weg, en dankzij de rustige vormgeving ook buiten het werk goed te dragen.'
}, {
  icon: KeyRoundIcon,
  title: 'Sleuteletuis',
  text: 'Een sleuteletui voorkomt krassen op telefoon en portemonnee en houdt losse sleutels bij elkaar. Zacht leer aan de buitenkant, stevige ringen of haken aan de binnenkant.'
}, {
  icon: RulerIcon,
  title: 'Riemen',
  text: 'Leren riemen in klassieke breedtes en kleuren, bedoeld om lang te dragen in plaats van per seizoen te vervangen. Ook geschikt bij een werktenue waarin een holster of etui aan de riem wordt gedragen.'
}];
const redenen = [{
  title: 'Ruim 25 jaar specialisme in leer',
  text: 'LeonDesign geeft aan meer dan 25 jaar ervaring te hebben als specialist in leer. Die ervaring zit vooral in de keuzes achter een product: welke indeling werkt, welke sluiting blijft goed sluiten en welke afwerking je jaren later nog terugziet.'
}, {
  title: 'Particulier én professioneel',
  text: 'Het assortiment bedient consumenten die een goede portemonnee zoeken, maar net zo goed horecaprofessionals, taxibedrijven en kappers die dagelijks met contant geld, bonnen en kleine spullen werken.'
}, {
  title: 'Functioneel en tijdloos',
  text: 'De producten combineren functionaliteit met een stijlvol en tijdloos ontwerp. Geen drukke details, wel een indeling die klopt en materiaal dat gebruikssporen goed verdraagt.'
}, {
  title: 'Webshop zonder openingstijden',
  text: `De webshop is 24/7 bereikbaar en de vestiging staat in ${site.city}, ${site.province}. Vragen over een model of een zakelijke bestelling kunnen per e-mail of telefoon.`
}];
export function Home() {
  return <>
      <Seo title="LeonDesign | Lederwaren uit Geleen — leren portemonnees & horeca lederwaren" description="LeonDesign uit Geleen is specialist in leren portemonnees, pasjeshouders, horeca lederwaren, riemen en leren accessoires voor particulieren en professionals." data-id="element-294" data-mp="3srkdhk9cib6o" />

      {/* Hero */}
      <Section className="pt-10 sm:pt-16" data-id="element-295" data-mp="ni4svdh0tq4">
        <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-14" data-id="element-296" data-mp="qnwmidhi9pj">
          <div data-id="element-297" data-mp="1h0ym0neudqya">
            <Eyebrow data-id="element-298" data-mp="1h4mgbos42he2">Lederwaren Geleen · Limburg</Eyebrow>
            <h1 className="font-display text-4xl font-semibold leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-id="element-299" data-mp="1c2x07uq17dj4">
              Leer dat meegaat met de jaren die u ermee maakt
            </h1>
            <p className="mt-6 max-w-prose text-lg leading-relaxed text-cocoa/90" data-id="element-300" data-mp="2cdhvtxc4fbqy">
              LeonDesign is een Nederlandse lederwarenwebshop uit Geleen. De collectie draait om
              portemonnees, pasjeshouders, sleuteletuis, riemen, heuptasjes en horeca lederwaren:
              artikelen die dagelijks worden gebruikt en daar ook op zijn gemaakt.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-id="element-301" data-mp="2vb2dajck381j">
              <ButtonLink to="/collectie" data-id="element-302" data-mp="2v9hg0lyuxazz">Bekijk de collectie</ButtonLink>
              <ButtonLink to="/contact" variant="secondary" data-id="element-303" data-mp="303c74agsks93">
                Neem contact op
              </ButtonLink>
            </div>
            <dl className="mt-10 grid max-w-lg grid-cols-2 gap-x-6 gap-y-5 border-t border-cocoa/15 pt-7 sm:grid-cols-3" data-id="element-304" data-mp="3a103f4u1q7td">
              <div data-id="element-305" data-mp="3e1k1camg1fgk">
                <dt className="text-sm text-cocoa/70" data-id="element-306" data-mp="3e34vhz4uiwdw">Specialisme</dt>
                <dd className="mt-1 font-display text-lg font-semibold text-ink" data-id="element-307" data-mp="12fhux6mqhd82">Lederwaren</dd>
              </div>
              <div data-id="element-308" data-mp="ycbpg17g3q2n">
                <dt className="text-sm text-cocoa/70" data-id="element-309" data-mp="ybtmklqa2dlr">Vestiging</dt>
                <dd className="mt-1 font-display text-lg font-semibold text-ink" data-id="element-310" data-mp="tdicc9w2m130">Geleen</dd>
              </div>
              <div data-id="element-311" data-mp="og8ogyobxxha">
                <dt className="text-sm text-cocoa/70" data-id="element-312" data-mp="ogryzubnhjla">Webshop</dt>
                <dd className="mt-1 font-display text-lg font-semibold text-ink" data-id="element-313" data-mp="1m38t6qp37h2t">24/7 open</dd>
              </div>
            </dl>
          </div>

          <figure className="overflow-hidden rounded-3xl border border-cocoa/10 bg-paper" data-id="element-314" data-mp="1h5z4xedfpbgk">
            <img src={images.cover} alt="Overzicht van leren portemonnees, pasjeshouders en paspoorthoesjes van LeonDesign" className="h-full w-full object-cover" width={1600} height={600} data-id="element-315" data-mp="1cbbgffifkh1z" />
          </figure>
        </div>
      </Section>

      {/* Introductie */}
      <Section className="pt-20 sm:pt-24" data-id="element-316" data-mp="3ua2iota2n9ty">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-16" data-id="element-317" data-mp="3ub45tw4d3ms1">
          <div data-id="element-318" data-mp="25qhoa5oj94j">
            <Eyebrow data-id="element-319" data-mp="26a1jwew47l7">Wie wij zijn</Eyebrow>
            <Heading data-id="element-320" data-mp="3kkso96rr9f2o">Een lederwarenspecialist met een uitgesproken voorkeur voor bruikbaarheid</Heading>
          </div>
          <Prose data-id="element-321" data-mp="3pjcbrblzyyx5">
            <p data-id="element-322" data-mp="3piu9nycnlqhb">
              Een portemonnee is een van de weinige voorwerpen die u elke dag in handen heeft. Toch
              wordt er vaak weinig over nagedacht tot het moment dat de naad losraat of het muntvak
              niet meer sluit. LeonDesign kijkt daar anders naar: het uitgangspunt is een artikel dat
              na jaren nog steeds prettig werkt, en dat er dan alleen maar beter uitziet.
            </p>
            <p data-id="element-323" data-mp="36ljil116e3cy">
              Het assortiment bestaat uit portemonnees, pasjeshouders, sleuteletuis, heuptasjes,
              riemen, paspoorthoesjes, pennenzakken en horeca accessoires. Die combinatie is bewust:
              particulieren zoeken vooral een goede dagelijkse portemonnee, terwijl professionals in
              de horeca, het taxivervoer en de kappersbranche behoefte hebben aan leer dat een
              volledige werkdag doorstaat.
            </p>
            <p data-id="element-324" data-mp="1nq5fjwwbemlp">
              Wilt u het volledige aanbod met actuele modellen en kleuren bekijken? Dat vindt u in de{' '}
              <TextLink href={site.website} data-id="element-325" data-mp="1ixm0evhzbjes">lederwaren webshop van LeonDesign</TextLink>. Op deze
              informatiesite leest u vooral welk type product bij welk gebruik past, zodat u met een
              gerichter beeld verder kijkt.
            </p>
          </Prose>
        </div>
      </Section>

      {/* Leerexpertise */}
      <Section className="pt-20 sm:pt-24" data-id="element-326" data-mp="26mexwwe91zbi">
        <div className="rounded-3xl bg-sand/70 p-7 sm:p-12" data-id="element-327" data-mp="22o7hy3rm121d">
          <div className="grid gap-10 lg:grid-cols-[1fr_1fr] lg:gap-14" data-id="element-328" data-mp="caqhsqwa7qpj">
            <div data-id="element-329" data-mp="cb0526pxzjrj">
              <Eyebrow data-id="element-330" data-mp="ccjm3zjf9yud">Kennis van leer</Eyebrow>
              <Heading data-id="element-331" data-mp="8a6grc8l82y4">Waar u op let bij een leren product</Heading>
              <Prose className="mt-5" data-id="element-332" data-mp="wube8a973dds">
                <p data-id="element-333" data-mp="wta202gg0kuh">
                  Leer is een natuurlijk materiaal en dat merkt u aan alles: aan de kleurdiepte, aan
                  de manier waarop een portemonnee zich naar zijn inhoud vormt en aan de glans die
                  na verloop van tijd op de meest gebruikte plekken ontstaat. Kleine kleurverschillen
                  horen daarbij en zijn geen gebrek.
                </p>
                <p data-id="element-334" data-mp="dwio68jo6xxt">
                  Bij het beoordelen van een leren artikel zijn drie dingen doorslaggevend: de
                  stevigheid van het stiksel, de afwerking van de randen en de kwaliteit van de
                  sluiting of rits. Juist daar wordt bezuinigd bij goedkope producten, en juist daar
                  gaat een product als eerste stuk.
                </p>
              </Prose>
            </div>
            <ul className="space-y-5" data-id="element-335" data-mp="1yastov2ebbze">
              {[{
              t: 'Kies op indeling, niet op formaat',
              d: 'Tel eerst hoeveel pasjes u dagelijks echt gebruikt en of u contant geld bij u draagt. Een te ruime portemonnee raakt vanzelf gevuld met spullen die u nooit nodig heeft.'
            }, {
              t: 'Onderhoud is een kwestie van weinig doen',
              d: 'Droog afnemen, niet laten uitdrogen op een verwarming en af en toe een leervet of -crème gebruiken. Meer heeft glad leer zelden nodig.'
            }, {
              t: 'Zakelijk gebruik vraagt om andere eisen',
              d: 'Wordt een artikel elke dienst gedragen, dan tellen bevestiging, sluiting en slijtvastheid zwaarder dan het uiterlijk. Voor horeca- en taxigebruik is dat het startpunt.'
            }].map(item => <li key={item.t} className="rounded-2xl bg-paper p-5" data-id="element-336" data-mp="3f3619rz12y7j">
                  <h3 className="font-display text-lg font-semibold text-ink" data-id="element-337" data-mp="3f1bpb35qigfl">{item.t}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-cocoa/85" data-id="element-338" data-mp="3b2m26iuw9kir">{item.d}</p>
                </li>)}
            </ul>
          </div>
        </div>
      </Section>

      {/* Productgroepen */}
      <Section className="pt-20 sm:pt-24" data-id="element-339" data-mp="8pcdfdr3zbk9">
        <div className="max-w-2xl" data-id="element-340" data-mp="mn0xijdcqeo9">
          <Eyebrow data-id="element-341" data-mp="mlzmgewc5s6j">De hoofdlijnen van het assortiment</Eyebrow>
          <Heading data-id="element-342" data-mp="hopqshicska9">Zeven productgroepen, elk met een eigen gebruik</Heading>
          <p className="mt-4 text-[17px] leading-relaxed text-cocoa/85" data-id="element-343" data-mp="3q380soruu9wi">
            Hieronder staat per groep waar het artikel voor bedoeld is. Een uitgebreidere
            beschrijving per categorie leest u op de{' '}
            <TextLink to="/collectie" data-id="element-344" data-mp="3oyh2syvdq22">pagina met de complete collectie</TextLink>.
          </p>
        </div>

        <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-345" data-mp="3v1ssfzt1jl9c">
          {productgroepen.map(({
          icon: Icon,
          title,
          text
        }) => <li key={title} className="flex flex-col rounded-2xl border border-cocoa/12 bg-paper p-6" data-id="element-346" data-mp="37anczlr0iuzk">
              <Icon className="h-6 w-6 text-pine" aria-hidden="true" data-id="element-347" data-mp="1hvmjlmge93kd" />
              <h3 className="mt-4 font-display text-xl font-semibold text-ink" data-id="element-348" data-mp="1mmb76qoydge8">{title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-cocoa/85" data-id="element-349" data-mp="18v6g4cznhwfi">{text}</p>
            </li>)}
        </ul>
      </Section>

      {/* Horeca uitgelicht */}
      <Section className="pt-20 sm:pt-24" data-id="element-350" data-mp="1cx1ep9q75vpl">
        <div className="grid items-center gap-10 rounded-3xl border border-cocoa/12 bg-paper p-6 sm:p-10 lg:grid-cols-[1fr_1.15fr] lg:gap-14" data-id="element-351" data-mp="20lufpkloetr2">
          <figure className="overflow-hidden rounded-2xl" data-id="element-352" data-mp="25kxb0dbutllu">
            <img src={images.horecaBar} alt="Leren kelnerportemonnees en holsters van LeonDesign op een bar" className="h-full w-full object-cover" width={1024} height={1536} loading="lazy" data-id="element-353" data-mp="1rm7f310rtrz2" />
          </figure>
          <div data-id="element-354" data-mp="l5vlld8w8y9t">
            <Eyebrow data-id="element-355" data-mp="l6dgo6jtl6g5">Horeca lederwaren</Eyebrow>
            <Heading data-id="element-356" data-mp="2murq0dygbp3b">Gemaakt voor een volle dienst, niet voor de etalage</Heading>
            <Prose className="mt-5" data-id="element-357" data-mp="2mwvqelx565yu">
              <p data-id="element-358" data-mp="2hv6gj9lnnycc">
                Achter de bar en op het terras telt vooral snelheid. Een kelnerportemonnee moet in
                één beweging open kunnen, munten mogen niet blijven haken en de bonnenhouder moet
                bereikbaar blijven als het druk is. De horeca lederwaren van LeonDesign zijn op dat
                ritme ingedeeld.
              </p>
              <p data-id="element-359" data-mp="241x9hfl6jowz">
                Dezelfde logica geldt voor taxichauffeurs en kappers: beroepen waarin contant geld,
                bonnen en kleine gereedschappen de hele dag in en uit een etui gaan. Een holster of
                heuptasje van leer houdt dat bij elkaar zonder dat u telkens hoeft te zoeken.
              </p>
            </Prose>
            <div className="mt-7" data-id="element-360" data-mp="3oj3liettjzym">
              <ButtonLink to="/collectie" variant="secondary" data-id="element-361" data-mp="3ojbwg16dsv76">
                Alle horeca-artikelen bekijken
              </ButtonLink>
            </div>
          </div>
        </div>
      </Section>

      {/* Waarom */}
      <Section className="pt-20 sm:pt-24" data-id="element-362" data-mp="168xvgzid7bet">
        <div className="max-w-2xl" data-id="element-363" data-mp="1b8trabgwrwja">
          <Eyebrow data-id="element-364" data-mp="1b7r0s4nhzw3w">Waarom LeonDesign</Eyebrow>
          <Heading data-id="element-365" data-mp="1g023tgdjby0o">Wat deze specialist onderscheidt</Heading>
        </div>
        <div className="mt-10 grid gap-x-12 gap-y-9 md:grid-cols-2" data-id="element-366" data-mp="1k25pextx4izo">
          {redenen.map(r => <div key={r.title} className="border-t border-cocoa/15 pt-6" data-id="element-367" data-mp="nbwiarenq4zs">
              <h3 className="font-display text-xl font-semibold text-ink" data-id="element-368" data-mp="ndzgvztvl6g4">{r.title}</h3>
              <p className="mt-3 text-[16px] leading-relaxed text-cocoa/85" data-id="element-369" data-mp="sb9ei2rf4of1">{r.text}</p>
            </div>)}
        </div>
      </Section>

      {/* Klantreacties (aangeleverd beeldmateriaal) */}
      <Section className="pt-20 sm:pt-24" data-id="element-370" data-mp="11cz556dh7jbr">
        <div className="max-w-2xl" data-id="element-371" data-mp="27s9gk5px6q9s">
          <Eyebrow data-id="element-372" data-mp="27r85i18wm3s2">Gedeeld door LeonDesign</Eyebrow>
          <Heading data-id="element-373" data-mp="2cqkqshk4n2sj">Klantreacties uit de eigen kanalen</Heading>
          <p className="mt-4 text-[17px] leading-relaxed text-cocoa/85" data-id="element-374" data-mp="2cozyyzp75xyd">
            Onderstaande afbeeldingen zijn door LeonDesign zelf gepubliceerd beeldmateriaal met
            reacties van klanten.
          </p>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2" data-id="element-375" data-mp="2kadgxo6jrju1">
          <figure className="overflow-hidden rounded-2xl border border-cocoa/12 bg-paper" data-id="element-376" data-mp="2kbwzxmjl88zi">
            <img src={images.reviewKarin} alt="Door LeonDesign gedeelde klantreactie van Karin over service en levering" className="w-full object-cover" loading="lazy" data-id="element-377" data-mp="2g774fysucwf4" />
          </figure>
          <figure className="overflow-hidden rounded-2xl border border-cocoa/12 bg-paper" data-id="element-378" data-mp="33y341i3jgvwx">
            <img src={images.reviewMartin} alt="Door LeonDesign gedeelde klantreactie van Martin over keuze en bestelproces" className="w-full object-cover" loading="lazy" data-id="element-379" data-mp="2yzi31gggq87f" />
          </figure>
        </div>
      </Section>

      {/* CTA + interne links */}
      <Section className="pt-20 sm:pt-24" data-id="element-380" data-mp="3rlgicgv0ddh9">
        <div className="rounded-3xl bg-ink px-6 py-12 text-cream sm:px-12" data-id="element-381" data-mp="3rmj98osbzfx6">
          <div className="max-w-2xl" data-id="element-382" data-mp="e40s0q7qy0t">
            <h2 className="font-display text-3xl font-semibold leading-tight sm:text-4xl" data-id="element-383" data-mp="d2ltl62trdt">
              Vraag over een model, een maat of een zakelijke bestelling?
            </h2>
            <p className="mt-4 text-[17px] leading-relaxed text-cream/85" data-id="element-384" data-mp="3moq1vsfakfvb">
              Bel gerust, of stuur een e-mail met uw vraag. Voor horeca-, taxi- en kappersbedrijven
              is het meestal handig om eerst het gebruik door te nemen; daarna is de keuze snel
              gemaakt.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-id="element-385" data-mp="2yv7uq9hpe0va">
              <ButtonLink href={site.phoneHref} className="!bg-sage !text-ink hover:!bg-cream" data-id="element-386" data-mp="33x6wjexsopq4">
                Bel {site.phone}
              </ButtonLink>
              <ButtonLink href={`mailto:${site.email}`} className="!border !border-cream/30 !bg-transparent !text-cream hover:!bg-cream/10" data-id="element-387" data-mp="2uvh72enh40uz">
                Mail {site.email}
              </ButtonLink>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-2" data-id="element-388" data-mp="qpfh2dlk6ymx">
          <NextPage to="/over-ons" label="Over LeonDesign" description="Het verhaal achter de webshop in Geleen, de werkwijze en de klanten die er bediend worden." data-id="element-389" data-mp="qpyylt7arl0f" />
          <NextPage to="/collectie" label="Collectie & toepassingen" description="Alle productgroepen uitgebreid beschreven, van portemonnees tot horeca lederwaren." data-id="element-390" data-mp="1jdblk4hdduu9" />
          <NextPage to="/bronnen" label="Bronnen & vermeldingen" description="Waar LeonDesign nog meer online te vinden is, geordend per soort platform." data-id="element-391" data-mp="1af9ep8ayg7z2" />
          <NextPage to="/contact" label="Contact & locatie" description="Adres in Geleen, telefoonnummer, e-mail en bereikbaarheid van de webshop." data-id="element-392" data-mp="3v1ayghqkezax" />
        </div>
      </Section>
    </>;
}